export interface IEligibleDriver {
  id?: number;
  login?: string;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  registry?: string | null;
  identificationCode?: string | null;
  // Driver's current open window: the coupling (null on a legacy solo window) and the truck/asset plate.
  currentCouplingId?: number | null;
  currentTruck?: string | null;
}

export const driverDisplayName = (driver?: IEligibleDriver | null): string => {
  if (!driver) {
    return '';
  }
  const name = [driver.firstName, driver.lastName].filter(Boolean).join(' ').trim();
  return name || driver.login || String(driver.id ?? '');
};

// e.g. "Matrícula 1234 • motorista@empresa.com"
export const driverSecondaryLabel = (driver?: IEligibleDriver | null): string => {
  if (!driver) {
    return '';
  }
  const hasName = Boolean([driver.firstName, driver.lastName].filter(Boolean).join('').trim());
  return [driver.registry ? `Matrícula ${driver.registry}` : null, hasName ? driver.email || driver.login : null].filter(Boolean).join(' • ');
};

// One live link, mirroring DriverAssignmentListItemDTO. id is the assignment id, not the coupling id.
// Serves the legacy solo rows (GET /driver-assignments/{id}) in the delete dialog.
export interface IDriverAssignmentListItem {
  id?: number;
  userId?: number | null;
  driverName?: string | null;
  couplingId?: number | null;
  truck?: string | null;
  implement?: string | null;
  implement2?: string | null;
  implement3?: string | null;
  implement4?: string | null;
  implement5?: string | null;
  implement6?: string | null;
  companyId?: number | null;
  companyName?: string | null;
  validFrom?: string | null;
}

// Mirrors DriverAssignmentCompositionDTO. On create only userId/companyId/truck are required; on edit
// implements may be filled, replaced or cleared with null.
export interface IDriverAssignmentComposition {
  userId?: number | null;
  companyId?: number | null;
  truck?: string | null;
  implement?: string | null;
  implement2?: string | null;
  implement3?: string | null;
  implement4?: string | null;
  implement5?: string | null;
  implement6?: string | null;
  validFrom?: string | null;
}

// UTC midnight matches the day grain used server-side (ceilDay / KpiEventData); local midnight would
// shift the informed day by one.
export const toUtcDayInstant = (date?: string | null): string | null => (date ? `${date}T00:00:00.000Z` : null);

export const toUtcDayInput = (instant?: string | null): string => (instant ? instant.substring(0, 10) : '');

// One row of the unified list, mirroring CouplingAssignmentRowDTO. rowKey is 'C' + couplingId for a coupling
// and 'A' + assignmentId for a legacy window pointing straight at an asset (couplingId null).
export interface ICouplingAssignmentRow {
  rowKey: string;
  couplingId?: number | null;
  assignmentId?: number | null;
  userId?: number | null;
  driverName?: string | null;
  truck?: string | null;
  implement?: string | null;
  implement2?: string | null;
  implement3?: string | null;
  implement4?: string | null;
  implement5?: string | null;
  implement6?: string | null;
  implementCount?: number;
  companyId?: number | null;
  companyName?: string | null;
  validFrom?: string | null;
  couplingCreated?: string | null;
}

// Mirrors AssignmentVehicleOptionDTO; every current* field is null when the vehicle is free. A slot prefilled
// from a row carries { id: null, licensePlate, currentCouplingId, currentCouplingTruck } so the move chips
// still know where it comes from.
export interface IAssignmentVehicleOption {
  id?: number | null;
  licensePlate?: string | null;
  hasVirtualCoupling?: boolean;
  currentCouplingId?: number | null;
  currentCouplingTruck?: string | null;
  currentDriverId?: number | null;
  currentDriverName?: string | null;
}

export interface IUnassignedSummary {
  drivers: number;
  trucksWithoutDriver: number;
  implementsWithoutCoupling: number;
}

export type CouplingDeleteScope = 'DRIVER' | 'COUPLING';

export const implementPlates = (row?: Partial<ICouplingAssignmentRow> | null): string[] =>
  row ? [row.implement, row.implement2, row.implement3, row.implement4, row.implement5, row.implement6].filter((plate): plate is string => Boolean(plate)) : [];

export type FormFocusTarget = 'company' | 'driver' | 'truck' | 'implement';
export type FormNextTarget = Exclude<FormFocusTarget, 'company'>;

export interface IFormQuery {
  companyId: number | null;
  companyName: string;
  focus: FormFocusTarget | null;
  next: FormNextTarget | null;
}

const FOCUS_VALUES: FormFocusTarget[] = ['company', 'driver', 'truck', 'implement'];
const NEXT_VALUES: FormNextTarget[] = ['driver', 'truck', 'implement'];

// Unknown values become null; `next` only means something after `focus=company`.
export const parseFormQuery = (search: string): IFormQuery => {
  const params = new URLSearchParams(search);
  const rawCompanyId = params.get('companyId');
  const companyId = rawCompanyId != null && rawCompanyId !== '' && !isNaN(Number(rawCompanyId)) ? Number(rawCompanyId) : null;
  const rawFocus = params.get('focus') as FormFocusTarget | null;
  const focus = rawFocus && FOCUS_VALUES.includes(rawFocus) ? rawFocus : null;
  const rawNext = params.get('next') as FormNextTarget | null;
  const next = focus === 'company' && rawNext && NEXT_VALUES.includes(rawNext) ? rawNext : null;
  return { companyId, companyName: params.get('companyName') ?? '', focus, next };
};
